<div class="space-y-6">
  <app-page-header [backTo]="['/my-vehicles']" [backLabel]="'vehicles.retourAMaFlotte' | t" />

  @if (loadError() || loading()) {
    <app-data-state [loading]="loading()" [error]="loadError()" (retry)="fetch()" />
  } @else if (vehicle(); as v) {
    <!-- Identity + actions -->
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-4">
          @if (v.imgCover) {
            <img [src]="v.imgCover" [alt]="displayName()" class="h-24 w-32 rounded-lg object-cover" />
          } @else {
            <span class="flex h-24 w-32 items-center justify-center rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-800">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M6.5 5.75A1.25 1.25 0 0 0 5.3 6.62l-1.05 3.13H3.75a.75.75 0 0 0-.75.75V14a.75.75 0 0 0 .75.75H4.5v1.5a1.25 1.25 0 1 0 2.5 0v-1.5h10v1.5a1.25 1.25 0 1 0 2.5 0v-1.5h.75A.75.75 0 0 0 21 14v-3.5a.75.75 0 0 0-.75-.75h-.5L18.7 6.62A1.25 1.25 0 0 0 17.5 5.75h-11Z"/></svg>
            </span>
          }
          <div class="min-w-0">
            <h1 class="text-title-sm font-bold text-gray-800 dark:text-white/90 sm:text-title-md">{{ displayName() }}</h1>
            <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {{ v.year }} · <span class="font-mono">{{ v.licencePlate || '—' }}</span> · {{ v.location || '—' }}@if (v.lat && v.lng) { · <span class="font-mono text-xs">{{ v.lat }}, {{ v.lng }}</span> }
            </p>
            <div class="mt-2 flex flex-wrap items-center gap-2">
              <span
                class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                [class.bg-brand-50]="statusMeta[v.status].palette === 'brand'"
                [class.text-brand-700]="statusMeta[v.status].palette === 'brand'"
                [class.dark:bg-brand-500/15]="statusMeta[v.status].palette === 'brand'"
                [class.dark:text-brand-400]="statusMeta[v.status].palette === 'brand'"
                [class.bg-blue-light-50]="statusMeta[v.status].palette === 'blue-light'"
                [class.text-blue-light-700]="statusMeta[v.status].palette === 'blue-light'"
                [class.dark:bg-blue-light-500/15]="statusMeta[v.status].palette === 'blue-light'"
                [class.dark:text-blue-light-400]="statusMeta[v.status].palette === 'blue-light'"
                [class.bg-warning-50]="statusMeta[v.status].palette === 'warning'"
                [class.text-warning-700]="statusMeta[v.status].palette === 'warning'"
                [class.dark:bg-warning-500/15]="statusMeta[v.status].palette === 'warning'"
                [class.dark:text-warning-400]="statusMeta[v.status].palette === 'warning'"
                [class.bg-gray-100]="statusMeta[v.status].palette === 'gray'"
                [class.text-gray-700]="statusMeta[v.status].palette === 'gray'"
                [class.dark:bg-gray-800]="statusMeta[v.status].palette === 'gray'"
                [class.dark:text-gray-300]="statusMeta[v.status].palette === 'gray'"
                [class.bg-error-50]="statusMeta[v.status].palette === 'error'"
                [class.text-error-700]="statusMeta[v.status].palette === 'error'"
                [class.dark:bg-error-500/15]="statusMeta[v.status].palette === 'error'"
                [class.dark:text-error-400]="statusMeta[v.status].palette === 'error'"
              >
                {{ statusMeta[v.status].label | t }}
              </span>
              @if (v.avgRating != null && v.avgRating > 0) {
                <span class="inline-flex items-center gap-1 rounded-full bg-warning-50 px-2.5 py-1 text-xs font-medium text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                  ★ {{ v.avgRating | number:'1.1-1' }}
                </span>
              }
              @if (v.featured) {
                <span class="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400" [title]="'vehicles.selectionneALaUne' | t">
                  {{ 'vehicles.aLaUne' | t }}
                </span>
              }
              @if (v.docsExpired) {
                <span class="inline-flex items-center gap-1 rounded-full bg-error-50 px-2.5 py-1 text-xs font-medium text-error-700 dark:bg-error-500/15 dark:text-error-400" [title]="'vehicles.unDocumentObligatoireEst' | t">
                  {{ 'vehicles.documentsExpires' | t }}
                </span>
              }
              @if (v.provisionExpired) {
                <span class="inline-flex items-center gap-1 rounded-full bg-error-50 px-2.5 py-1 text-xs font-medium text-error-700 dark:bg-error-500/15 dark:text-error-400" [title]="'vehicles.laFenetreDeMise' | t">
                  {{ 'vehicles.miseADispoExpiree' | t }}
                </span>
              }
            </div>
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          @if (canPublish()) {
            <button type="button" (click)="publish()" [disabled]="publishing()" class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
              @if (publishing()) { Publication… } @else { Publier }
            </button>
          }
          @if (canUnpublish()) {
            <button type="button" (click)="unpublish()" [disabled]="publishing()" class="inline-flex items-center gap-2 rounded-lg border border-warning-300 px-4 py-2 text-sm font-medium text-warning-700 transition hover:bg-warning-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-warning-800 dark:text-warning-400 dark:hover:bg-warning-500/10">
              @if (publishing()) { Retrait… } @else { {{ 'vehicles.retirerDuCatalogue' | t }} }
            </button>
          }
          @if (canMaintain()) {
            <button type="button" (click)="maintain()" [disabled]="publishing()" class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
              Mettre en maintenance
            </button>
          }
          @if (canEndMaintain()) {
            <button type="button" (click)="publish()" [disabled]="publishing()" class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
              {{ 'vehicles.finDeMaintenance' | t }}
            </button>
          }
          <a [routerLink]="['/my-vehicles', v.id, 'edit']" class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
            {{ 'vehicles.modifier' | t }}
          </a>
          <button type="button" (click)="openDelete()" class="inline-flex items-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-700 transition hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10">
            {{ 'vehicles.supprimer' | t }}
          </button>
        </div>
      </div>

      @if (publishError()) {
        <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
          {{ publishError() }}
        </div>
      }
      @if (v.status === 'AVAILABLE' && v.rejectionReason) {
        <!-- previously rejected then approved — show history but neutral -->
      } @else if (canResubmit() && v.rejectionReason) {
        <!-- Rejet récent : message clair + CTA "Resoumettre" en gros -->
        <div class="mt-4 rounded-xl border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-500/10">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-error-800 dark:text-error-300">
                {{ 'vehicles.vehiculeRejeteParLa' | t }}
              </p>
              <p class="mt-1 text-xs text-error-700 dark:text-error-400">
                <strong>Motif :</strong> {{ v.rejectionReason }}
              </p>
              <p class="mt-2 text-xs text-gray-600 dark:text-gray-400">
                {{ 'vehicles.corrigeLesInformationsOu' | t }}
              </p>
            </div>
            <button type="button" (click)="resubmit()" [disabled]="publishing()" class="inline-flex h-fit items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
              @if (publishing()) { Resoumission… } @else { {{ 'vehicles.resoumettreALaRevue' | t }} }
            </button>
          </div>
        </div>
      } @else if (v.status === 'PRE_UNDER_REVIEW') {
        <!-- Brouillon initial sans rejet : juste un bouton "Soumettre" -->
        <div class="mt-4 rounded-xl border border-warning-200 bg-warning-50 p-4 dark:border-warning-800 dark:bg-warning-500/10">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p class="text-sm text-warning-800 dark:text-warning-300">
              {{ 'vehicles.brouillonLeVehiculeDoit' | t }}
            </p>
            <button type="button" (click)="resubmit()" [disabled]="publishing()" class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
              @if (publishing()) { Soumission… } @else { {{ 'vehicles.soumettreALaRevue' | t }} }
            </button>
          </div>
        </div>
      } @else if (v.rejectionReason) {
        <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
          <strong>{{ 'vehicles.motifDuRejetAdmin' | t }}</strong> {{ v.rejectionReason }}
        </div>
      }
    </section>

    <!-- Documents expirés (#55) — véhicule masqué + non réservable -->
    @if (v.docsExpired) {
      <section class="rounded-2xl border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-500/10">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-error-800 dark:text-error-300">
              {{ 'vehicles.documentsExpiresVehiculeMasque' | t }}
            </p>
            <p class="mt-1 text-xs text-error-700 dark:text-error-400">
              {{ 'vehicles.unDocumentObligatoireAssurance' | t }}
              <strong>{{ 'vehicles.retireDeLaRecherche' | t }}</strong> et <strong>{{ 'vehicles.nonReservable' | t }}</strong> {{ 'vehicles.jusquAuRenouvellementMettez' | t }}
            </p>
          </div>
          <a [routerLink]="['/my-vehicles', v.id, 'edit']" class="inline-flex h-fit items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600">
            {{ 'vehicles.renouvelerMesDocuments' | t }}
          </a>
        </div>
      </section>
    }

    <!-- Mise à disposition expirée (#59) — véhicule masqué + non réservable -->
    @if (v.provisionExpired && !resignaturePending()) {
      <section class="rounded-2xl border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-500/10">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-error-800 dark:text-error-300">
              {{ 'vehicles.miseADispositionExpiree' | t }}
            </p>
            <p class="mt-1 text-xs text-error-700 dark:text-error-400">
              {{ 'vehicles.laFenetreDeMise' | t }}
            </p>
          </div>
          <a [routerLink]="['/my-vehicles', v.id, 'edit']" class="inline-flex h-fit items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600">
            {{ 'vehicles.prolongerLaMiseA' | t }}
          </a>
        </div>
      </section>
    }

    <!-- Contrat de mise à disposition (Docusign / Docuseal) -->
    @if (contractPending()) {
      <section class="rounded-2xl border border-warning-200 bg-warning-50 p-4 dark:border-warning-800 dark:bg-warning-500/10">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div class="min-w-0 flex-1">
            @if (resignaturePending()) {
              <p class="text-sm font-medium text-warning-800 dark:text-warning-300">
                {{ 'vehicles.prolongationDeMiseA' | t }}
              </p>
              <p class="mt-1 text-xs text-warning-700 dark:text-warning-400">
                {{ 'vehicles.pendingPeriodIntro' | t }}
                @if (v.pendingProvisionStartDate || v.pendingProvisionEndDate) {
                  (<strong>{{ (v.pendingProvisionStartDate | ldate: 'dd/MM/yyyy') || '…' }}
                  → {{ (v.pendingProvisionEndDate | ldate: 'dd/MM/yyyy') || '…' }}</strong>)
                }
                {{ 'vehicles.pendingPeriodOutro' | t }}
                {{ 'vehicles.pendingPeriodEnd' | t }}
              </p>
            } @else {
              <p class="text-sm font-medium text-warning-800 dark:text-warning-300">
                {{ 'vehicles.contratDeMiseA' | t }}
              </p>
              <p class="mt-1 text-xs text-warning-700 dark:text-warning-400">
                {{ 'vehicles.signeLeContratPour' | t }}
              </p>
            }
          </div>
          <button
            type="button"
            (click)="openSign()"
            [disabled]="signRefreshing()"
            class="inline-flex h-fit items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            @if (signRefreshing()) { {{ 'vehicles.verification' | t }} } @else { {{ resignaturePending() ? 'Signer la prolongation' : 'Signer le contrat' }} }
          </button>
        </div>
        @if (v.docusignSubmissionStatus === 'declined') {
          <p class="mt-3 text-xs text-error-700 dark:text-error-400">
            {{ 'vehicles.laSignaturePrecedenteA' | t }}
          </p>
        }
        @if (signError()) {
          <p class="mt-3 rounded-md border border-error-200 bg-error-50 px-2 py-1 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/20 dark:text-error-400">
            {{ signError() }}
          </p>
        }
      </section>
    } @else if (contractSigned()) {
      <section class="rounded-2xl border border-brand-200 bg-brand-50 p-4 dark:border-brand-800 dark:bg-brand-500/10">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-brand-800 dark:text-brand-300">
              {{ 'vehicles.contratDeMiseA2' | t }}
            </p>
            <p class="mt-1 text-xs text-brand-700 dark:text-brand-400">
              {{ 'vehicles.leContratPdfEst' | t }}
            </p>
          </div>
          <a
            [href]="v.provisionContract"
            target="_blank"
            rel="noopener"
            class="inline-flex h-fit items-center gap-2 rounded-lg border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 transition hover:bg-brand-100 dark:border-brand-800 dark:text-brand-400 dark:hover:bg-brand-500/20"
          >
            {{ 'vehicles.telechargerLePdf' | t }}
          </a>
        </div>
      </section>
    }

    <!-- Galerie photos -->
    <app-vehicle-photos
      [vehicleId]="v.id"
      [currentCoverUrl]="v.imgCover ?? null"
      (coverChanged)="onCoverChanged($event)"
    />

    <!-- Équipements -->
    <app-vehicle-options
      [vehicleId]="v.id"
      [currentOptions]="v.options"
      (optionsChanged)="onOptionsChanged($event)"
    />

    <!-- Disponibilité (calendrier d'occupation) -->
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">{{ 'vehicles.disponibilite' | t }}</h2>
      <app-vehicle-availability
        [vehicleId]="v.id"
        [provisionStart]="v.provisionStartDate ?? null"
        [provisionEnd]="v.provisionEndDate ?? null"
      />
    </section>

    <!-- Pricing + key stats -->
    <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.tarifJour' | t }}</p>
        <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">
          {{ v.dailyRate | number:'1.0-0' }} <span class="text-sm font-normal text-gray-400">XOF</span>
        </p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.caution' | t }}</p>
        @if (depositRate(); as dr) {
          @if (dr.effectiveRatePct > 0) {
            <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">{{ dr.effectiveRatePct }} <span class="text-sm font-normal text-gray-400">%</span></p>
            <p class="text-xs text-gray-500 dark:text-gray-400">≈ {{ dr.exampleDeposit | number:'1.0-0' }} XOF / jour de loc.</p>
          } @else {
            <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">—</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ 'vehicles.aucuneCaution' | t }}</p>
          }
        } @else {
          <p class="mt-1 text-sm text-gray-400">—</p>
        }
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.carburant' | t }}</p>
        <p class="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
          {{ v.fuelType ? fuelLabel[v.fuelType] : '—' }}
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400">{{ v.fuelConsumptionRate || '—' }} L/100km</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.boite' | t }}</p>
        <p class="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
          {{ v.gearbox ? gearLabel[v.gearbox] : '—' }}
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400">{{ v.cylinderCapacity || '—' }} cc</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.portes' | t }}</p>
        <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">{{ v.numberOfDoors || '—' }}</p>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- Identité technique -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Identification</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.categorie' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.category?.entityCode || v.category?.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.type' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.type?.entityCode || v.type?.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.marque' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.make?.entityCode || v.make?.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.modele' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.model?.entityCode || v.model?.name || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.annee' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.year }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.plaque' | t }}</dt>
            <dd class="mt-1 font-mono text-sm text-gray-800 dark:text-white/90">{{ v.licencePlate || '—' }}</dd>
          </div>
        </dl>
      </section>

      <!-- Notes opérationnelles -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">{{ 'vehicles.notesVehicule010' | t }}</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.securite' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.securityRate || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Confort</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.comfortRate || '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Conso</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ v.fuelConsumptionRate || '—' }} L/100km</dd>
          </div>
        </dl>
      </section>

      <!-- Politique d'annulation (effective : override véhicule sinon globale) -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <div class="mb-4 flex items-center justify-between gap-3">
          <h2 class="text-sm font-semibold text-gray-800 dark:text-white/90">Politique d'annulation</h2>
          @if (effectivePolicy(); as ep) {
            @if (v.cancellationPolicy) {
              <span class="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ 'vehicles.personnalisee' | t }}</span>
            } @else {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">{{ 'vehicles.globaleDefaut' | t }}</span>
            }
          }
        </div>
        @if (effectivePolicy(); as ep) {
          <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ ep.name || '—' }}</p>
          @if (ep.description) {
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ ep.description }}</p>
          }
          <dl class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Remb. total</dt>
              <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">≥ {{ ep.hoursBeforeStartForFullRefund ?? 0 }} h → {{ ep.fullRefundPercentage ?? 100 }} %</dd>
            </div>
            @if (ep.hoursBeforeStartForPartialRefund != null && ep.partialRefundPercentage != null) {
              <div>
                <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Remb. partiel</dt>
                <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ ep.noRefundUnderHours ?? 0 }}–{{ ep.hoursBeforeStartForPartialRefund }} h → {{ ep.partialRefundPercentage }} %</dd>
              </div>
            }
            <div>
              <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Frais fixe</dt>
              <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">{{ (ep.cancellationFee ?? 0) | number:'1.0-0' }} XOF</dd>
            </div>
          </dl>
        } @else {
          <p class="text-sm text-gray-400">{{ 'vehicles.politiqueIndisponible' | t }}</p>
        }
      </section>

      <!-- Documents -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">{{ 'vehicles.documents' | t }}</h2>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.carteGrise' | t }}</p>
            @if (v.registrationCard?.length) {
              <ul class="mt-2 space-y-1">
                @for (img of v.registrationCard; track img.id || img.url) {
                  @if (img.url) {
                    <li><a [href]="img.url" target="_blank" class="text-xs text-brand-600 hover:underline dark:text-brand-400">{{ 'vehicles.voirLeDocument' | t }}</a></li>
                  }
                }
              </ul>
            } @else {
              <p class="mt-2 text-xs text-gray-400">—</p>
            }
          </div>
          <div>
            <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.assurance' | t }}</p>
            @if (v.insuranceExpiryDate) {
              <p class="mt-0.5 text-xs font-medium"
                [class.text-error-600]="isExpired(v.insuranceExpiryDate)"
                [class.dark:text-error-400]="isExpired(v.insuranceExpiryDate)"
                [class.text-gray-600]="!isExpired(v.insuranceExpiryDate)"
                [class.dark:text-gray-400]="!isExpired(v.insuranceExpiryDate)">
                {{ 'vehicles.expiresOn' | t: { date: (v.insuranceExpiryDate | ldate: 'dd MMM yyyy') ?? '' } }}{{ isExpired(v.insuranceExpiryDate) ? ('vehicles.expiredSuffix' | t) : '' }}
              </p>
            }
            @if (v.insurance?.length) {
              <ul class="mt-2 space-y-1">
                @for (img of v.insurance; track img.id || img.url) {
                  @if (img.url) {
                    <li><a [href]="img.url" target="_blank" class="text-xs text-brand-600 hover:underline dark:text-brand-400">{{ 'vehicles.voirLeDocument' | t }}</a></li>
                  }
                }
              </ul>
            } @else {
              <p class="mt-2 text-xs text-gray-400">—</p>
            }
          </div>
          <div>
            <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.visiteTechnique' | t }}</p>
            @if (v.technicalInspectionExpiryDate) {
              <p class="mt-0.5 text-xs font-medium"
                [class.text-error-600]="isExpired(v.technicalInspectionExpiryDate)"
                [class.dark:text-error-400]="isExpired(v.technicalInspectionExpiryDate)"
                [class.text-gray-600]="!isExpired(v.technicalInspectionExpiryDate)"
                [class.dark:text-gray-400]="!isExpired(v.technicalInspectionExpiryDate)">
                {{ 'vehicles.expiresOn' | t: { date: (v.technicalInspectionExpiryDate | ldate: 'dd MMM yyyy') ?? '' } }}{{ isExpired(v.technicalInspectionExpiryDate) ? ('vehicles.expiredSuffix' | t) : '' }}
              </p>
            }
            @if (v.technicalInspection?.length) {
              <ul class="mt-2 space-y-1">
                @for (img of v.technicalInspection; track img.id || img.url) {
                  @if (img.url) {
                    <li><a [href]="img.url" target="_blank" class="text-xs text-brand-600 hover:underline dark:text-brand-400">{{ 'vehicles.voirLeDocument' | t }}</a></li>
                  }
                }
              </ul>
            } @else {
              <p class="mt-2 text-xs text-gray-400">—</p>
            }
          </div>
        </div>
        <!-- Lien PDF contrat déplacé dans la section "Contrat" en haut de page (cf. contractSigned bandeau). -->
      </section>

      <!-- Workflow / métadonnées -->
      <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] lg:col-span-2">
        <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">{{ 'vehicles.workflowMetadonnees' | t }}</h2>
        <dl class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">ID</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">#{{ v.id }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.creeLe' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.createdAt ? (v.createdAt | ldate: 'dd MMM yyyy, HH:mm') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.modifieLe' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.updatedAt ? (v.updatedAt | ldate: 'dd MMM yyyy, HH:mm') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.miseADispoDebut' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.provisionStartDate ? (v.provisionStartDate | ldate: 'dd MMM yyyy') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.miseADispoFin' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.provisionEndDate ? (v.provisionEndDate | ldate: 'dd MMM yyyy') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.revueLe' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              {{ v.reviewedAt ? (v.reviewedAt | ldate: 'dd MMM yyyy, HH:mm') : '—' }}
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'vehicles.statutContrat' | t }}</dt>
            <dd class="mt-1 text-sm text-gray-800 dark:text-white/90">
              @switch (v.docusignSubmissionStatus) {
                @case ('completed') { {{ 'vehicles.signe' | t }} }
                @case ('awaiting') { En attente de signature }
                @case ('declined') { {{ 'vehicles.refuse' | t }} }
                @default { — }
              }
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">QR code</dt>
            <dd class="mt-1 font-mono text-xs text-gray-500 break-all dark:text-gray-400">{{ v.qrcode || '—' }}</dd>
          </div>
        </dl>
      </section>
    </div>
  }
</div>

<!-- Delete modal -->
<app-confirm-modal
  [open]="deleteOpen()"
  [busy]="deleting()"
  [error]="deleteError()"
  [title]="'vehicles.supprimerCeVehicule' | t"
  [confirmLabel]="'vehicles.supprimerDefinitivement' | t"
  busyLabel="Suppression…"
  danger
  (confirm)="confirmDelete()"
  (close)="closeDelete()"
>
  {{ 'vehicles.leVehicule' | t }} <span class="font-medium">{{ displayName() }}</span> {{ 'vehicles.seraRetireDeLa' | t }}
</app-confirm-modal>

<!--
  Modal "en attente de signature" — la signature elle-même se passe dans
  un onglet Docuseal séparé (cf. openSign() qui fait `window.open`).

  Pourquoi pas une iframe ? On avait des 422 silencieux à la soumission,
  causés par la perte de cookies/CSRF en contexte iframe cross-origin.
  L'onglet popup garantit un contexte browser propre.

  Comment on sait que c'est signé ? Polling de /link-contract toutes les
  3 s + un poll immédiat sur `window.focus` (= retour sur l'onglet owner).
  Dès que `docusignSubmissionStatus` passe à completed/declined/expired,
  on referme automatiquement ce dialog.
-->
<app-modal [isOpen]="signOpen()" (close)="closeSign()" className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-dark">
  <div class="flex flex-col items-center gap-4 text-center">
    <div class="flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
      <svg class="h-7 w-7 animate-spin" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
        <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
      </svg>
    </div>
    <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">
      Signature en cours…
    </h3>
    <p class="text-sm text-gray-600 dark:text-gray-300">
      {{ 'vehicles.termineLaSignatureDans' | t }}
    </p>

    @if (signError(); as msg) {
      <div class="w-full rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
        {{ msg }}
      </div>
    }

    <div class="flex w-full flex-col gap-2 pt-2 sm:flex-row sm:justify-center">
      <button
        type="button"
        (click)="reopenSignPopup()"
        class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
      >
        {{ 'vehicles.rouvrirLaFenetreDe' | t }}
      </button>
      <button
        type="button"
        (click)="closeSign()"
        class="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        {{ 'vehicles.jAiTermine' | t }}
      </button>
    </div>

    <p class="text-[11px] text-gray-400 dark:text-gray-500">
      {{ 'vehicles.astuceSiLOnglet' | t }}
    </p>
  </div>
</app-modal>
